import { Fragment, type FC } from "react";
import PaginationThemes from "./config";
import type { ProductPaginationProps, PropsTheme } from "./Types";
import ResponsiveDropMenu from "../DropMenu";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";

const Pagination: FC<ProductPaginationProps> = ({
  currentPage,
  totalPages,
  totalResults,
  resultsPerPage,
  onPageChange,
  onLimitChange,
  type,
}) => {
  if (totalResults === 0) {
    return null;
  }
  const handlePageChange = (page: number) => {
    onPageChange(page);
    // Smooth scroll to top when changing page
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleLimitChange = (limit: number) => {
    onLimitChange(limit);
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };
  const theme: PropsTheme = PaginationThemes[type || "products"];
  const limitOptions = theme.limitOptions;

  // Calculate the range of displayed results
  const startResult = (currentPage - 1) * resultsPerPage + 1;
  const endResult = Math.min(currentPage * resultsPerPage, totalResults);

  // Generate page numbers to display with better logic
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 7; // Show more pages for better navigation

    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is less than max visible
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      // Calculate the range around current page
      const delta = 2; // Show 2 pages before and after current
      const rangeStart = Math.max(2, currentPage - delta);
      const rangeEnd = Math.min(totalPages - 1, currentPage + delta);

      // Add ellipsis after first page if needed
      if (rangeStart > 2) {
        pages.push("...");
      }

      // Add pages around current page
      for (let i = rangeStart; i <= rangeEnd; i++) {
        pages.push(i);
      }

      // Add ellipsis before last page if needed
      if (rangeEnd < totalPages - 1) {
        pages.push("...");
      }

      // Always show last page (if not already included)
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }

    return pages;
  };

  // Ensure totalPages is at least 1 if there are results
  const safeTotalPages = Math.max(1, totalPages);

  return (
    <div className="flex flex-col lg:flex-row justify-between items-center mt-8 gap-4 p-4 bg-neutral-900 border border-neutral-800 rounded-xl shadow-sm">
      {/* Results info and limit selector */}
      <div className="flex flex-col sm:flex-row items-center gap-4 w-full sm:w-auto">
        <div className="text-sm text-neutral-400 font-medium w-full sm:w-auto text-center sm:text-left">
          Showing{" "}
          <span className="text-white font-bold">
            {startResult}-{endResult}
          </span>{" "}
          of <span className="text-white font-bold">{totalResults}</span>{" "}
          {theme.label}
        </div>
        <div className="flex items-center justify-center gap-3 w-full sm:w-auto">
          <label className="text-sm text-neutral-400 whitespace-nowrap">
            Show:
          </label>
          <div className="w-32">
            <ResponsiveDropMenu
              value={resultsPerPage}
              onChange={(v) => handleLimitChange(Number(v))}
              options={limitOptions.map((option) => ({
                value: option,
                label: option.toString(),
              }))}
              className="w-full"
              buttonClassName="bg-neutral-800 border border-neutral-700 text-white hover:border-primary/50 focus:border-primary focus:ring-1 focus:ring-primary w-full justify-between"
            />
          </div>
          <span className="text-sm text-neutral-400 whitespace-nowrap hidden sm:inline">
            per page
          </span>
        </div>
      </div>

      {/* Pagination controls */}
      {(() => {
        const shouldShow =
          totalResults >= resultsPerPage || totalPages > 1 || currentPage > 1;
        return shouldShow;
      })() && (
        <div className="w-full sm:w-auto">
          {/* Compact mobile controls */}
          <div className="flex items-center justify-between sm:hidden w-full bg-neutral-800/50 p-2 rounded-lg border border-neutral-800">
            <button
              disabled={currentPage === 1}
              onClick={() => handlePageChange(currentPage - 1)}
              className="p-2 text-neutral-300 hover:text-white border border-neutral-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-neutral-700 transition-colors bg-neutral-800"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>

            <div className="text-sm font-medium text-white">
              Page {currentPage} / {safeTotalPages}
            </div>

            <button
              disabled={currentPage >= safeTotalPages}
              onClick={() => handlePageChange(currentPage + 1)}
              className="p-2 text-neutral-300 hover:text-white border border-neutral-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-neutral-700 transition-colors bg-neutral-800"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {/* Full controls for sm+ screens */}
          <div className="hidden sm:flex items-center gap-2">
            {/* First page button */}
            <button
              disabled={currentPage === 1}
              onClick={() => handlePageChange(1)}
              className="p-2 text-neutral-400 hover:text-white border border-neutral-800 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed hover:bg-neutral-800 transition-colors"
              title="First page"
            >
              <ChevronsLeft className="w-5 h-5" />
            </button>

            {/* Previous button */}
            <button
              disabled={currentPage === 1}
              onClick={() => handlePageChange(currentPage - 1)}
              className="px-3 py-2 text-sm font-medium text-neutral-300 hover:text-white border border-neutral-800 bg-neutral-800/50 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-neutral-800 transition-colors flex items-center gap-1"
            >
              <ChevronLeft className="w-4 h-4" />
              <span>Prev</span>
            </button>

            {/* Page numbers */}
            <div className="flex items-center gap-1 mx-2 bg-neutral-950/50 p-1 rounded-lg border border-neutral-800">
              {getPageNumbers().map((page, index) => (
                <Fragment key={index}>
                  {page === "..." ? (
                    <span className="px-3 py-2 text-sm text-neutral-600 font-medium">
                      ...
                    </span>
                  ) : (
                    <button
                      onClick={() => handlePageChange(page as number)}
                      className={`min-w-9 h-9 text-sm font-bold rounded-md transition-all duration-200 ${
                        currentPage === page
                          ? "bg-primary text-black shadow-md shadow-primary/20 scale-105"
                          : "text-neutral-400 hover:bg-neutral-800 hover:text-white"
                      }`}
                    >
                      {page}
                    </button>
                  )}
                </Fragment>
              ))}
            </div>

            {/* Next button */}
            <button
              disabled={currentPage >= safeTotalPages}
              onClick={() => handlePageChange(currentPage + 1)}
              className="px-3 py-2 text-sm font-medium text-neutral-300 hover:text-white border border-neutral-800 bg-neutral-800/50 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-neutral-800 transition-colors flex items-center gap-1"
            >
              <span>Next</span>
              <ChevronRight className="w-4 h-4" />
            </button>

            {/* Last page button */}
            <button
              disabled={currentPage >= safeTotalPages}
              onClick={() => handlePageChange(safeTotalPages)}
              className="p-2 text-neutral-400 hover:text-white border border-neutral-800 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed hover:bg-neutral-800 transition-colors"
              title="Last page"
            >
              <ChevronsRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Pagination;
